import type { Agent, AgentReadCursors, Persisted } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import { computeClearedCursors } from './clear-agent-unread.js'
import { deriveAgentUnread } from './use-agent-unread.js'

export interface AgentUnreadFlags {
  /** Whether the agent has activity newer than this user last saw */
  isUnread: (agentId: string) => boolean
  /** Mark the agent's activity as seen (bumps its read cursor) */
  acknowledge: (agent: Pick<Persisted<Agent>, 'id'>) => void
}

/**
 * Per-agent unread, for the agent switcher's dot (chat makeover #2718, C9a). Read-only apart from `acknowledge`:
 * the first-visit cursor stamp stays with `useAgentRoster`, which owns it once per host.
 */
export const useAgentUnreadFlags = (
  agents: Pick<Persisted<Agent>, 'id' | 'lastRunAt' | 'lastNudgeAt'>[]
): AgentUnreadFlags => {
  const [cursors, updateCursors] = useUserConfig<'agentReadCursors'>('agentReadCursors')
  const cursorMap = cursors as AgentReadCursors | undefined

  const isUnread = (agentId: string) => {
    const agent = agents.find(candidate => candidate.id === agentId)
    return agent ? deriveAgentUnread(agent, cursorMap?.[agentId]) : false
  }

  /**
   * Writes only when there is something to clear, so selecting an agent doesn't write user config every time. Built
   * from this render's cursors (never cached): the stamp in `useAgentRoster` writes the same map (Seymour).
   */
  const acknowledge = (agent: Pick<Persisted<Agent>, 'id'>) => {
    if (!isUnread(agent.id)) {
      return
    }
    updateCursors(computeClearedCursors(agent.id, cursorMap) as never)
  }

  return { isUnread, acknowledge }
}
